import { useEffect } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { LoadingScreen } from '../../components/skeletons/LoadingScreen';
import { useAuth } from './AuthProvider';
import { useRouteAuth } from './use-route-auth';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { requiresAuth } = useRouteAuth();
  const { isInProgress, login, silentAuthAttempted } = useAuth();
  const isAuthenticated = useIsAuthenticated();
  
  // Log the initial auth state for the route
  useEffect(() => {
    console.debug('[AUTH_GUARD] Route access check:', {
      path: window.location.pathname,
      requiresAuth,
      isAuthenticated,
      isInProgress,
      silentAuthAttempted,
      timestamp: new Date().toISOString()
    });
  }, [requiresAuth, isAuthenticated, isInProgress, silentAuthAttempted]);
  
  useEffect(() => {
    // Only attempt login if:
    // 1. On a protected route
    // 2. Not already authenticated
    // 3. Not in the middle of an interaction
    // 4. Silent auth has been attempted (to avoid race conditions)
    if (requiresAuth && !isAuthenticated && !isInProgress && silentAuthAttempted) {
      const currentUrl = window.location.pathname + window.location.search;
      
      console.debug('[AUTH_GUARD] Protected route requires login', {
        path: window.location.pathname,
        search: window.location.search,
        fullUrl: currentUrl,
        silentAuthAttempted,
        timestamp: new Date().toISOString()
      });
      
      login();
    }
  }, [requiresAuth, isAuthenticated, isInProgress, login, silentAuthAttempted]);

  // Show loading screen while authenticating on protected routes
  if (requiresAuth && !isAuthenticated) {
    console.debug('[AUTH_GUARD] Showing loading screen while authenticating', {
      path: window.location.pathname,
      timestamp: new Date().toISOString()
    });
    return <LoadingScreen />;
  }

  // Otherwise, render children directly
  return <>{children}</>;
}
